import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { BUY } from "@/constants/common";
import { getProfileSlug, saveMemberWantAd } from "@/services/v2/wantAdService";
import { getUserType } from "@/helpers/common";
import { fetchUserDetails } from "@/actions/userprofile";
import CustomLinkModal from "@/components/Common/CustomLinkModal";
import PremiumPlanModal from "@/components/PlanUpgrade/PremiumPlanModal";

const LINK_FAILED = "We couldn't create your buyer link. Please try again.";

const LINK_REQUEST = {
  isFilledByClient: true,
  isAgentAsSeller: false,
  IsSavedDraft: true,
  IsPublished: false,
  cmpldStep: 1,
  cmpldPerctg: 7,
  adType: BUY,
  isAgentCreated: true,
};

export const useBuyerFormLink = () => {
  const dispatch = useDispatch();
  const userType = getUserType();
  const userDetails = useSelector((state) => state.userProfile.userDetails);
  const [link, setLink] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    dispatch(fetchUserDetails());
  }, [reload]);

  const knownPro = useRef(null);
  const [wanted, setWanted] = useState(false);
  const isPro = useMemo(() => {
    if (userDetails?.length > 0) knownPro.current = userDetails[0]?.period !== 1 && userType === 2;
    return knownPro.current;
  }, [userDetails, userType]);

  const slugQuery = useQuery({
    queryKey: ["profileSlug"],
    queryFn: async () => _.get(await getProfileSlug(), "result", null),
  });

  const mutation = useMutation({
    mutationFn: () => saveMemberWantAd(LINK_REQUEST),
    onSuccess: (response) => {
      if (response?.errorCode === 0 && response?.result && slugQuery.data) {
        setLink(`${process.env.REACT_APP_BUYERLISTING_SERVICE_BASE_URL}/form/${slugQuery.data}`);
        setShowLink(true);
      } else {
        toast.error(LINK_FAILED);
      }
    },
    onError: () => toast.error(LINK_FAILED),
  });

  const send = () => {
    if (isPro === null) return setWanted(true);
    if (isPro !== true) return setShowPlan(true);
    if (!slugQuery.data || mutation.isPending) return setWanted(true);
    return mutation.mutate();
  };

  useEffect(() => {
    if (!wanted || isPro === null) return;
    if (isPro === true && !slugQuery.data) {
      if (slugQuery.isError || slugQuery.isSuccess) {
        setWanted(false);
        toast.error(LINK_FAILED);
      }
      return;
    }
    setWanted(false);
    send();
  }, [wanted, isPro, slugQuery.data, slugQuery.isError, slugQuery.isSuccess]);

  const modals = (
    <>
      <CustomLinkModal show={showLink} onHide={() => setShowLink(false)} linkToShare={link} />
      {showPlan ? (
        <PremiumPlanModal
          userDetails={userDetails?.[0]}
          show={showPlan}
          onHide={() => setShowPlan(false)}
          updateReloadParam={() => setReload((value) => value + 1)}
          showPlanModal={() => setShowPlan(true)}
          reason="buyerListingForm"
          feature="buyerForm"
        />
      ) : null}
    </>
  );

  return {
    isPro,
    send,
    sending: mutation.isPending || wanted,
    modals,
  };
};
